import { createSelector } from "@reduxjs/toolkit";
import { FormRootState } from "./formStore";
import { selectForm } from "./selectForm";
import { OncologyItem } from '@fusion-advantage/formlib'
import { useFormSelector } from "./formStoreHooks";

export const selectAllOncologyData = createSelector(
	(state: FormRootState) => selectForm(state).oncologyData,
	(oncologyData) => oncologyData,
)

const getUniqueValues = (data: OncologyItem[], field: keyof OncologyItem): string[] => {
	const carcinoid = '(carcinoid)'
	const values = data
		.map(item => item[field])
		.filter(item => item !== null)
		.sort((a, b) => {
			if (a === 'Other') return 1
			if (b === 'Other') return -1
			if (a === 'Unknown') return 1
			if (b === 'Unknown') return -1
			return (Number(a.endsWith(carcinoid)) - Number(b.endsWith(carcinoid))) || a.localeCompare(b);
		})
	return [...new Set(values)]
}

export const selectAllPrimaryTumors = createSelector(
	selectAllOncologyData,
	(oncologyData) => getUniqueValues(oncologyData, 'PrimaryTumor'),
)

export const selectAllMetastaticSites = createSelector(
	selectAllOncologyData,
	(oncologyData) => {
		return getUniqueValues(oncologyData, 'MetastaticSite')
	},
)

export const selectCarcinoidMetastaticSites = createSelector(
	selectAllOncologyData,
	(oncologyData) => {
		const carcinoidStr = "Carcinoid"
		const data = oncologyData.filter(item => item.PrimaryTumor === carcinoidStr)

		return getUniqueValues(data, 'MetastaticSite')
	}
)

export const selectAllTreatmentStatuses = createSelector(
	selectAllOncologyData,
	(oncologyData) => getUniqueValues(oncologyData, 'TreatmentStatus'),
)

export const selectCarcinoidMatch = (state: FormRootState, primaryTumor: string, metastaticSite: string | null, treatmentStatus: string) =>
	selectAllOncologyData(state).find(
		item => primaryTumor === "Carcinoid" &&
			item.PrimaryTumor === primaryTumor &&
			item.MetastaticSite === metastaticSite &&
			item.TreatmentStatus === treatmentStatus
	)

export const selectPrimaryTumorStatusMatch = (state: FormRootState, primaryTumor: string, treatmentStatus: string) =>
	selectAllOncologyData(state).find(
		item => item.PrimaryTumor === primaryTumor &&
			item.TreatmentStatus === treatmentStatus &&
			item.MetastaticSite === null
	)

export const selectMetastaticSiteStatusMatch = (state: FormRootState, metastaticSite: string | null, treatmentStatus: string | null) =>
	selectAllOncologyData(state).find(
		item => item.MetastaticSite === metastaticSite &&
			item.TreatmentStatus === treatmentStatus &&
			item.PrimaryTumor === null
	)

export const usePrimaryTumorStatusMatch = (primaryTumor: string, treatmentStatus: string) =>
	useFormSelector(state => selectPrimaryTumorStatusMatch(state, primaryTumor, treatmentStatus))

export const useMetastaticSiteStatusMatch = (metastaticSite: string | null, treatmentStatus: string | null) =>
	useFormSelector(state => selectMetastaticSiteStatusMatch(state, metastaticSite, treatmentStatus))

export const useCarcinoidMatch = (primaryTumor: string, metastaticSite: string | null, treatmentStatus: string) =>
	useFormSelector(state => selectCarcinoidMatch(state, primaryTumor, metastaticSite, treatmentStatus))